import { useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown } from 'lucide-react';
import { useFilters } from '@/lib/filterContext';
import { getCompanies, isSuperAdmin } from '@/services/authService';

// VG/Piele = Odoo; SB = Metabase (el BFF enruta por slug). Catálogo completo (solo
// super admin lo ve entero); el resto ve únicamente sus empresas del JWT.
const CATALOG = [
  { slug: 'pi', name: 'Pielé' },
  { slug: 'sb', name: 'Smart Bamboo' },
  { slug: 'vg', name: 'Vine Garden' },
];

export function CompanySelect({ fullWidth }: { fullWidth?: boolean } = {}) {
  const { companies, setCompanies } = useFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Super admin ve todo; el resto solo sus empresas (el BFF impone el scope real).
  const visible = isSuperAdmin() ? CATALOG : getCompanies();
  const ALL_SLUGS = visible.map((c) => c.slug);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // "Todas" = las 3 marcas seleccionadas (incluye Smart Bamboo). Mandar [] dejaría
  // fuera a SB (no es empresa Odoo del JWT), por eso se envían los 3 slugs explícitos.
  const all = ALL_SLUGS.every((s) => companies.includes(s));
  const label = all
    ? 'Todas las compañías'
    : visible.filter((c) => companies.includes(c.slug))
        .map((c) => c.name)
        .join(', ') || 'Selecciona empresa';

  const toggle = (slug: string) =>
    setCompanies(companies.includes(slug) ? companies.filter((c) => c !== slug) : [...companies, slug]);

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:border-slate-400 ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <Building2 className="h-4 w-4 shrink-0 text-red-600" />
        <span className={`truncate ${fullWidth ? 'min-w-0 flex-1 text-left' : 'max-w-[150px]'}`}>{label}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border-b border-slate-100 px-2 py-2 text-sm font-semibold hover:bg-slate-50">
            <input type="checkbox" className="accent-red-600" checked={all} onChange={() => setCompanies(ALL_SLUGS)} />
            <span>Todas las compañías</span>
          </label>
          {visible.map((c) => (
            <label
              key={c.slug}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
            >
              <input
                type="checkbox"
                className="accent-red-600"
                checked={companies.includes(c.slug)}
                onChange={() => toggle(c.slug)}
              />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
